import React, { useState } from "react";
//#region types
import { IconDefinitionConstants } from "utils/constants";
import { BaseInputProps } from "models/propTypes/wintFormPropsTypes";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { CircularProgress, Grid, InputAdornment, TextField, FormControl } from "@mui/material";
import NumberFormatField from "../../forms/baseFields/numberFormatField";
import StyledButton from "../../buttons/styledButton";
import Icon from "../../icon";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";

function BaseNumberInput(props: BaseInputProps) {
  const [hasFocus, setHasFocus] = useState(false);
  const variant = props.label ? "filled" : "outlined";

  const onCopyToClipBoard = () => {
    navigator.clipboard.writeText(props.value);
  };

  const onFocus = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    props.onFocus && props.onFocus(event);
    setHasFocus(true);
  };

  const onBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    props.onBlur && props.onBlur(event);
    setHasFocus(false);
  };

  const shrinkLabel = hasFocus || !UtilsHelper.isEmpty(props.value);

  const parts = LocalizationHelperInstance.getNumberSeparators();

  return (
    <FormControl fullWidth className={cssExports.inputFormControl}>
      <span className={clsx(cssExports.iconInputContainer, props.rootClass, props.className)}>
        <TextField
          fullWidth
          InputProps={{
            ...(props.InputProps ?? {}),
            value: props.value ?? "",
            name: props.name,
            type: props.type,
            error: props.hasError,
            disabled: props.disabled,
            readOnly: props.readonly,
            inputComponent: NumberFormatField,

            startAdornment: props.icon && !props.endIcon && (
              <InputAdornment position="start" className={cssExports.inputAdornedStart}>
                <Icon icon={props.icon} className={cssExports.inputIcon} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {props.endAdornment}

                {props.showCopyButton && (
                  <InputAdornment position="end" className={cssExports.inputAdornedStart}>
                    <StyledButton
                      size={"xs"}
                      className={clsx(cssExports.iconButton, cssExports.transparent)}
                      onClick={onCopyToClipBoard}
                    >
                      <Icon icon={IconDefinitionConstants.WCopy} />
                    </StyledButton>
                  </InputAdornment>
                )}

                {props.icon && props.endIcon && (
                  <InputAdornment position="end" className={cssExports.inputAdornedStart}>
                    <Icon icon={props.icon} className={cssExports.inputIcon} />
                  </InputAdornment>
                )}

                {props.isLoading && (
                  <InputAdornment position="end" className={cssExports.inputAdornedEnd}>
                    <CircularProgress size={20} className={cssExports.inputProgress} />
                  </InputAdornment>
                )}

                {props.disabled ? (
                  <InputAdornment position="end" className={clsx(cssExports.inputAdornedEnd, cssExports.disabledIcon)}>
                    <Icon icon={IconDefinitionConstants.WLock} className={cssExports.inputIcon} />
                  </InputAdornment>
                ) : props.success ? (
                  <InputAdornment position="end" className={clsx(cssExports.inputAdornedEnd, cssExports.success)}>
                    <Icon icon={IconDefinitionConstants.WCheck} className={cssExports.inputIcon} />
                  </InputAdornment>
                ) : props.hasError ? (
                  <InputAdornment position="end" className={clsx(cssExports.inputAdornedEnd, cssExports.error)}>
                    <Icon icon={IconDefinitionConstants.WExclamation} className={cssExports.inputIcon} />
                  </InputAdornment>
                ) : (
                  <></>
                )}
              </>
            ),
            classes: {
              root: clsx(
                cssExports.inputRoot,
                props.icon ? cssExports.iconInput : "",
                props.multiline ? cssExports.inputMultilineRoot : "",
                props.readonly ? cssExports.readOnlyInput : "",
                shrinkLabel && props.label ? cssExports.shrinkedLabelInput : "",
                props.rightAlign && cssExports.inputRight
              ),
              input: cssExports.inputInput,
              focused: cssExports.focusedInput,
              error: cssExports.errorInput,
              disabled: cssExports.disabledInput,
              notchedOutline: cssExports.notchedOutline,
            },
            onChange: props.onChange,
            onBlur: onBlur,
            onFocus: onFocus,
          }}
          InputLabelProps={{
            shrink: shrinkLabel,
            classes: {
              root: clsx(
                cssExports.inputLabel,
                props.icon && !props.endIcon ? cssExports.iconInputLabel : "",
                props.readonly ? cssExports.readOnlyLabel : "",
                props.rightAlign && cssExports.labelRight
              ),
              error: cssExports.errorLabel,
              shrink: cssExports.shrinkedLabel,
              focused: cssExports.focusedLabel,
            },
          }}
          inputProps={{
            ...(props.inputProps ?? {}),
            getInputRef: (props.inputProps as any)?.ref,
            maxLength:
              props.format === undefined
                ? props.maxLength ||
                  (props.decimalPoints ? 21 : 11) +
                    (props.prefix ? props.prefix.length : 0) +
                    (props.suffix ? props.suffix.length : 0) +
                    (props.decimalPoints ? props.decimalPoints + 1 : 0)
                : undefined,
            style: { textAlign: props.rightAlign ? "right" : "left" },
            allowNegative: props.allowNegative,
            decimalScale: props.decimalPoints ?? 0,
            fixedDecimalScale: props.fixedDecimalScale,
            thousandSeparator: parts.thousandSeparator,
            allowLeadingZeros: props.allowLeadingZeros,
            suffix: props.suffix ? ` ${props.suffix}` : undefined,
            prefix: props.prefix ? `${props.prefix} ` : undefined,
            decimalSeparator: parts.decimalSeparator,
            format: props.format,
            max: props.maxNumber,
          }}
          FormHelperTextProps={{
            classes: {
              root: cssExports.inputHelperText,
              error: cssExports.errorHelperText,
            },
          }}
          label={
            props.rightAlign ? (
              <Grid container justifyContent="flex-end" className={props.disabled ? cssExports.disableLabel : ""}>
                {props.label ?? (shrinkLabel ? undefined : props.placeholder)}
              </Grid>
            ) : shrinkLabel ? (
              props.label
            ) : (
              `${props.placeholder ?? props.label}${
                props.optional
                  ? `${props.placeholder || props.label ? " " : ""}(${LocalizationHelperInstance.getFormattedMessage(
                      TranslationKeys.Common.Optional
                    )})`
                  : ""
              }`
            )
          }
          error={props.hasError}
          variant={variant as any}
          autoComplete={"off"}
          data-testid={`${props.name}BaseNumberInput`}
        />
      </span>
    </FormControl>
  );
}

export default BaseNumberInput;
